import {
	PropsWithChildren,
	ReactElement,
	useContext,
	useEffect,
	useMemo
} from "react";
import classNames from "classnames";
import { WeekViewCard } from "./WeekViewCard";
import { DateString } from "../DateString/DateString";
import { useCurrentHour, useForceRerender } from "client/Hooks";
import {
	WeekViewContext,
	WeekViewContextProps,
	WeekViewMode
} from "./WeekViewContext";
import styles from "./WeekView.module.less";
import { clamp } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addTime,
	calculateTimeDifference,
	millisecondsInAMinute,
	millisecondsInAnHour
} from "client/DateAndTime/dateTimeUtils";
import { convertToDateTime } from "client/DateAndTime/timeUtils";
import { TimeOnly } from "client/DateAndTime/timeOnly";

export type WeekViewProps = {
	height?: "auto" | "fill";
	timezone: TimeZone;
} & ({
	mode?: "stacked" & WeekViewMode;
} | {
	mode: "hours" & WeekViewMode;
	startHour: number;
	endHour: number;
});

export const WeekView = (props: PropsWithChildren<WeekViewProps>): ReactElement => {
	const {
		mode = "stacked",
		children,
		height = "auto",
		timezone
	} = props;

	const contextValue: WeekViewContextProps = useMemo(
		() => ({ mode }),
		[mode]
	);

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles.root_withHourTrack]: mode === "hours",
					[styles.root_shouldFillHeight]: height === "fill"
				}
			)}
		>
			{ props.mode === "hours" && (
				<>
					<WeekViewTimeIndicator
						start={props.startHour}
						end={props.endHour}
						timezone={timezone}
					/>
					<WeekViewHourTrack
						start={props.startHour}
						end={props.endHour}
						timezone={timezone}
					/>
				</>
			)}
			<WeekViewContext.Provider value={contextValue}>

				{ children }
			</WeekViewContext.Provider>
		</div>
	);
};
WeekView.displayName = "WeekView";

type WeekViewDayProps = {
	title: string;
	isToday?: boolean;
	dayInTheMonth: number;
};

const WeekViewDay = (props: PropsWithChildren<WeekViewDayProps>): ReactElement => {
	const {
		title,
		isToday = false,
		children,
		dayInTheMonth
	} = props;

	const { mode } = useContext(WeekViewContext);

	return (
		<>
			<div
				className={classNames(
					styles.header,
					{
						[styles.header_today]: isToday
					}
				)}
			>
				<p className={styles.header_day}>{title} </p>
				<h4>{dayInTheMonth}</h4>
			</div>
			<div
				className={classNames(
					styles.content,
					{
						[styles.content_withHourLines]: mode === "hours",
					}
				)}
			>
				{ children }
			</div>
		</>
	);
};
WeekViewDay.displayName = "WeekViewDay";

type WeekViewHourTrackProps = {
	start: number;
	end: number;
	timezone: TimeZone;
};

const WeekViewHourTrack = (props: WeekViewHourTrackProps): ReactElement => {
	const {
		start,
		end,
		timezone
	} = props;

	const currentHour: number = useCurrentHour(timezone);

	const blocks = useMemo(
		(): readonly HourBlock[] => {
			const startTime: Date = convertToDateTime(
				new TimeOnly(start, 0, 0, 0),
				timezone
			);

			const startTimeMs: number = startTime.getTime();

			const result: HourBlock[] = [];

			for (let i = start; i <= end; i++) {
				const date = new Date(startTimeMs + ((i - start) * millisecondsInAnHour));

				result.push({
					date,
					hour: i
				});
			}

			return result;
		},
		[
			start,
			timezone,
			end
		]
	);

	return (
		<>
			<div
				className={styles.header}
			/>
			<div
				className={classNames(
					styles.content,
					styles.content_withHourLines,
					styles.content_hourTrack
				)}
			>
				{ blocks.map((x) => (
					<WeekViewTimeBlock
						key={x.date.toISOString()}
						durationInMinutes={MINUTES_PER_HOUR}
					>
						<div
							className={classNames(
								styles.hourTrackBlock,
								{ [styles.hourTrackBlock_current]: x.hour === currentHour }
							)}
						>
							<DateString
								value={x.date}
								format="T"
								timezone={timezone}
							/>
						</div>
					</WeekViewTimeBlock>
				))}
			</div>
		</>
	);
};
WeekViewHourTrack.displayName = "WeekViewHourTrack";

type HourBlock = {
	date: Date;
	hour: number;
};

const MINUTES_PER_HOUR = 60;

type WeekViewTimeIndicatorProps = {
	start: number;
	end: number;
	timezone: TimeZone;
};

const WeekViewTimeIndicator = (props: WeekViewTimeIndicatorProps): ReactElement => {
	const {
		start,
		end,
		timezone
	} = props;

	const [rerender] = useForceRerender();

	const now = new Date();

	const startTime: Date = convertToDateTime(
		new TimeOnly(start, 0, 0, 0),
		timezone,
		now
	);

	const endTime: Date = convertToDateTime(
		new TimeOnly(end, 0, 0, 0),
		timezone,
		now
	);

	let msTillTopOfTheMinute: number = millisecondsInAMinute - (now.getTime() % millisecondsInAMinute);

	if (msTillTopOfTheMinute === 0) {
		msTillTopOfTheMinute = millisecondsInAMinute;
	}

	const timeoutHandle: SetTimeoutHandle = setTimeout(rerender, msTillTopOfTheMinute);

	useEffect(
		() => {
			return () => clearTimeout(timeoutHandle);
		},
		[timeoutHandle]
	);

	const minutesSinceStart: number = calculateTimeDifference(
		now,
		startTime,
		"minute"
	);

	const top: number = clamp(
		minutesSinceStart,
		0,
		calculateTimeDifference(
			addTime(
				endTime,
				1,
				"hour"
			),
			startTime,
			"minute"
		)
			- 1
	);

	return (
		<div className={styles.timeIndicatorHost}>
			<div
				className={styles.timeIndicator}
				style={{
					top
				}}
			/>
		</div>
	);
};
WeekViewTimeIndicator.displayName = "WeekViewTimeIndicator";

export type WeekViewTimeBlockProps = {
	durationInMinutes: number;
};

const WeekViewTimeBlock = (props: PropsWithChildren<WeekViewTimeBlockProps>): ReactElement => {
	const {
		durationInMinutes,
		children
	} = props;

	return (
		<div
			className={styles.timeBlock}
			style={{
				height: durationInMinutes
			}}
		>
			{ children }
		</div>
	);
};
WeekViewTimeBlock.displayName = "WeekViewTimeBlock";

WeekView.Day = WeekViewDay;
WeekView.TimeBlock = WeekViewTimeBlock;
WeekView.Card = WeekViewCard;
